/**
 * Inventory Items Service Utilities
 *
 * Helper functions for inventory item operations, formatting, and calculations
 */

import type {
  InventoryItem,
  InventoryItemType,
  InventoryItemStatus,
  InventoryItemCategory,
  InventoryItemsListResponse,
  UpdateInventoryItemRequest
} from './types';
import { CATEGORY_METADATA, TYPE_TO_CATEGORY } from './types';
import { inventoryKeys } from '@/lib/query-keys';

/**
 * **The item's type slug, off the string-or-object union** (VALE-1032).
 *
 * `InventoryItem.type` is either the slug or the whole type record depending on
 * which endpoint served it, and `'other'` is the fallback every reader of it has
 * always used. Exported here so a new call site binds to one definition rather
 * than adding to the private copies this codebase already carries (four, at
 * time of writing — folding those in is its own sweep, not this ticket's).
 */
export function inventoryTypeSlug(item: InventoryItem): string {
  return typeof item.type === 'string' ? item.type : item.type?.slug || 'other';
}

// Status Priority Constants

/**
 * Status priority for determining primary status when institution has multiple items
 *
 * STRATEGY: Show LEAST progressed status to highlight what needs attention
 * Higher number = higher priority for display = less progressed = needs action
 *
 * Example: Institution with [draft, notified, collected] will show "Draft"
 * to highlight the item that needs the most attention.
 */
export const STATUS_PRIORITY: Record<InventoryItemStatus, number> = {
  archived: -1, // Lowest - don't show unless all items archived
  'not-held': 1, // Terminal state - no action needed
  'not-owed': 1, // Terminal state - liability doesn't exist
  collected: 2, // Completed - no action needed
  resolved: 2, // Completed - share lifecycle done
  settled: 2, // Completed - liability paid
  identified: 3, // In progress - some action may be needed
  confirmed: 3, // In progress - liability amount confirmed
  notified: 4, // Awaiting response - needs follow-up
  draft: 5 // HIGHEST - needs immediate action!
};

/**
 * Determines the primary status to display for an institution
 * based on the statuses of all its inventory items.
 *
 * Returns the LEAST progressed status (highest priority) to highlight
 * items that need attention.
 *
 * @param items - Inventory items for the institution
 * @returns The status with the highest priority (least progressed), or undefined if no items
 *
 * @example
 * // Institution with mixed statuses
 * const items = [
 *   { status: 'collected' },
 *   { status: 'draft' },
 *   { status: 'notified' }
 * ];
 * determinePrimaryStatus(items); // Returns 'draft' (highest priority)
 */
export function determinePrimaryStatus(
  items: InventoryItem[]
): InventoryItemStatus | undefined {
  if (items.length === 0) return undefined;

  return items.reduce(
    (primaryStatus, item) => {
      if (!item.status) return primaryStatus;

      const itemPriority = STATUS_PRIORITY[item.status] || 0;
      const primaryPriority = primaryStatus
        ? STATUS_PRIORITY[primaryStatus] || 0
        : -Infinity;

      return itemPriority > primaryPriority ? item.status : primaryStatus;
    },
    undefined as InventoryItemStatus | undefined
  );
}

// Formatting Functions

/**
 * Format inventory item status
 */
export function formatInventoryStatus(status: InventoryItemStatus): string {
  const statusLabels: Record<InventoryItemStatus, string> = {
    draft: 'Draft',
    notified: 'Notified',
    identified: 'Identified',
    'not-held': 'Not Held',
    collected: 'Collected',
    resolved: 'Resolved',
    archived: 'Archived',
    confirmed: 'Confirmed',
    settled: 'Settled',
    'not-owed': 'Not Owed'
  };

  return statusLabels[status] || status;
}

/**
 * Format category name
 */
export function formatCategoryName(category: InventoryItemCategory): string {
  return CATEGORY_METADATA[category]?.label || category;
}

/**
 * Get category icon
 */
export function getCategoryIcon(category: InventoryItemCategory): string {
  return CATEGORY_METADATA[category]?.icon || 'Archive';
}

/**
 * Get category color
 */
export function getCategoryColor(category: InventoryItemCategory): string {
  return CATEGORY_METADATA[category]?.color || 'gray';
}

// Calculation Functions

/**
 * Calculate net worth (assets - liabilities)
 */
export function calculateNetWorth(items: InventoryItem[]): number {
  return items.reduce((total, item) => total + (item.value || 0), 0);
}

/**
 * Calculate total assets (positive values only)
 */
export function calculateTotalAssets(items: InventoryItem[]): number {
  return items
    .filter((item) => (item.value || 0) > 0)
    .reduce((total, item) => total + (item.value || 0), 0);
}

/**
 * Calculate total liabilities (negative values only)
 */
export function calculateTotalLiabilities(items: InventoryItem[]): number {
  return Math.abs(
    items
      .filter((item) => (item.value || 0) < 0)
      .reduce((total, item) => total + (item.value || 0), 0)
  );
}

// Validation Functions

/**
 * Whether an inventory item is currently in a state where the
 * `POST /inventory-items/:id/mark-not-held` mutation will succeed. Terminal
 * states (already `not-held`, `collected`, or `archived`) and the liability
 * terminal states (`settled` / `not-owed`) reject the transition with
 * `INVALID_STATE_TRANSITION` (BE-6). The FE pre-disables the CTA in those
 * cases per LF-3701 § 3 acceptance criteria.
 */
const NON_MARK_NOT_HELD_STATUSES = new Set<InventoryItemStatus>([
  'not-held',
  'collected',
  'archived',
  'settled',
  'not-owed'
]);

export function canMarkInventoryItemNotHeld(
  status: InventoryItemStatus | undefined
): boolean {
  if (!status) return false;
  return !NON_MARK_NOT_HELD_STATUSES.has(status);
}

/**
 * Whether an inventory item may be (soft-)deleted from the list actions menu
 * (LF-5334). A `collected` asset has realised cash tracked against it, so
 * deleting it would orphan that record — archive it instead. Referenced items
 * and items in an active payout are guarded server-side (409
 * `assertAssetNotInActivePayout`); that rejection surfaces via the delete
 * mutation's error toast rather than a pre-disable, since the reference state
 * isn't carried on the Current View row.
 */
export function canDeleteInventoryItem(
  status: InventoryItemStatus | undefined
): boolean {
  return status !== 'collected';
}

// Data Transformation Functions

/**
 * Extract value from itemInfo based on inventory item type
 * Handles various field names used by the backend for different asset types
 */
type ItemInfoValueFields = {
  balanceAtDateOfDeath?: number;
  valueAtDateOfDeath?: number;
  currentBalance?: number;
  accountBalance?: number;
  estimatedValue?: number;
  totalValue?: number;
  valueOfSecurities?: number;
  coverAmount?: number;
  sumInsured?: number;
  bondAmount?: number;
  value?: number;
  portfolio?: Array<{ valueAtDateOfDeath?: number; value?: number }>;
};

/**
 * First field in priority order that actually holds a number, defaulting to 0.
 *
 * Replaces the `a || b || c || 0` chains below: `||` treats a stored `0` as
 * absent and falls through to the NEXT field, so an account confirmed at nil at
 * date of death reported its (non-zero) current balance instead — a wrong figure,
 * not merely a missing one (LF-6093).
 */
function firstRecorded(...candidates: Array<number | undefined>): number {
  for (const candidate of candidates) {
    if (typeof candidate === 'number') return candidate;
  }
  return 0;
}

export function extractValueFromItemInfo(
  type: InventoryItemType | string,
  itemInfo: ItemInfoValueFields | null | undefined
): number {
  if (!itemInfo) return 0;

  // The backend uses inconsistent field names between schemas and actual data
  // Priority order for estate management: date of death values > current values > generic values

  switch (type) {
    case 'bank-account':
      // Bank accounts: Backend uses balanceAtDateOfDeath (not valueAtDateOfDeath as in schema)
      // Priority: balanceAtDateOfDeath > valueAtDateOfDeath > currentBalance
      return firstRecorded(
        itemInfo.balanceAtDateOfDeath,
        itemInfo.valueAtDateOfDeath,
        itemInfo.currentBalance
      );

    case 'superannuation':
      // Super funds: Backend may use accountBalance instead of valueAtDateOfDeath
      // Priority: valueAtDateOfDeath > accountBalance > value
      return firstRecorded(
        itemInfo.valueAtDateOfDeath,
        itemInfo.accountBalance,
        itemInfo.value
      );

    case 'real-estate':
    case 'vehicle':
      // Property/vehicles: valueAtDateOfDeath > estimatedValue > value
      return firstRecorded(
        itemInfo.valueAtDateOfDeath,
        itemInfo.estimatedValue,
        itemInfo.value
      );

    case 'srn-shares':
    case 'hin-shares':
      // Shares: valueAtDateOfDeath > totalValue > valueOfSecurities > value
      return firstRecorded(
        itemInfo.valueAtDateOfDeath,
        itemInfo.totalValue,
        itemInfo.valueOfSecurities,
        itemInfo.value
      );

    case 'life-insurance':
      // Insurance: valueAtDateOfDeath > coverAmount > sumInsured > value
      return firstRecorded(
        itemInfo.valueAtDateOfDeath,
        itemInfo.coverAmount,
        itemInfo.sumInsured,
        itemInfo.value
      );

    case 'cryptocurrency':
      // Crypto: May have portfolio array with individual coin values
      if (itemInfo.portfolio && Array.isArray(itemInfo.portfolio)) {
        const totalValue = itemInfo.portfolio.reduce(
          (
            sum: number,
            coin: { valueAtDateOfDeath?: number; value?: number }
          ) => sum + firstRecorded(coin.valueAtDateOfDeath, coin.value),
          0
        );
        if (totalValue > 0) return totalValue;
      }
      return firstRecorded(
        itemInfo.valueAtDateOfDeath,
        itemInfo.totalValue,
        itemInfo.value
      );

    case 'nursing-home-bond':
      // Nursing home bonds: valueAtDateOfDeath > bondAmount > value
      return firstRecorded(
        itemInfo.valueAtDateOfDeath,
        itemInfo.bondAmount,
        itemInfo.value
      );

    case 'other':
      // Other assets: value is often the primary field (as seen in your example)
      return firstRecorded(
        itemInfo.value,
        itemInfo.valueAtDateOfDeath,
        itemInfo.estimatedValue
      );

    default:
      // Fallback: try all common field names
      return firstRecorded(
        itemInfo.valueAtDateOfDeath,
        itemInfo.balanceAtDateOfDeath,
        itemInfo.value,
        itemInfo.accountBalance,
        itemInfo.estimatedValue,
        itemInfo.currentBalance
      );
  }
}

type ItemInfoDescriptionFields = {
  description?: string;
  accountName?: string;
  bsb?: string;
  streetAddress?: string;
  suburb?: string;
  state?: string;
  numberOfShares?: number | string;
  shareRegistry?: string;
  fundName?: string;
  make?: string;
  model?: string;
  year?: number | string;
  policyNumber?: string;
  currency?: string;
  amount?: number | string;
  facilityName?: string;
  itemName?: string;
  name?: string;
};

/**
 * Generate description from itemInfo based on inventory item type
 */
export function generateDescriptionFromItemInfo(
  type: InventoryItemType | string,
  itemInfo: ItemInfoDescriptionFields | null | undefined
): string {
  if (!itemInfo) return `${type.replace('-', ' ')} item`;

  // Use explicit description if provided
  if (itemInfo.description) return itemInfo.description;

  // Generate description based on asset type
  switch (type) {
    case 'bank-account':
      const accountName = itemInfo.accountName || 'Bank Account';
      const bsb = itemInfo.bsb ? ` (${itemInfo.bsb})` : '';
      return `${accountName}${bsb}`;
    case 'real-estate':
      const address = itemInfo.streetAddress || 'Property';
      const suburb = itemInfo.suburb ? `, ${itemInfo.suburb}` : '';
      const state = itemInfo.state ? `, ${itemInfo.state}` : '';
      return `${address}${suburb}${state}`;
    case 'srn-shares':
    case 'hin-shares':
      const shares = itemInfo.numberOfShares
        ? `${itemInfo.numberOfShares} shares`
        : 'Shares';
      const registry = itemInfo.shareRegistry
        ? ` - ${itemInfo.shareRegistry}`
        : '';
      return `${shares}${registry}`;
    case 'superannuation':
      return (
        itemInfo.fundName || itemInfo.accountName || 'Superannuation Account'
      );
    case 'vehicle':
      const make = itemInfo.make || '';
      const model = itemInfo.model || '';
      const year = itemInfo.year ? `${itemInfo.year} ` : '';
      return `${year}${make} ${model}`.trim() || 'Vehicle';
    case 'life-insurance':
      return itemInfo.policyNumber
        ? `Policy ${itemInfo.policyNumber}`
        : 'Life Insurance Policy';
    case 'cryptocurrency':
      const currency = itemInfo.currency || 'Cryptocurrency';
      const amount = itemInfo.amount ? ` (${itemInfo.amount})` : '';
      return `${currency}${amount}`;
    case 'nursing-home-bond':
      return itemInfo.facilityName
        ? `${itemInfo.facilityName} - Accommodation Bond`
        : 'Nursing Home Bond';
    case 'other':
      return itemInfo.itemName || itemInfo.name || 'Other Asset';
    default:
      return `${type.replace('-', ' ')} item`;
  }
}

/**
 * **Apply an update to a cached item the way the server will apply it.**
 *
 * The optimistic mirror of `PUT /matters/:matterId/inventory-items/:id`, and
 * the only sanctioned way to build one. It exists because the endpoint behaves
 * as a PATCH in two places a plain `{ ...item, ...updates }` gets wrong:
 *
 * · **`itemInfo` is MERGED, not replaced** — the service writes
 *   `{ ...existingItemInfo, ...incoming }`, so a body carrying one changed key
 *   leaves its siblings alone. Spreading the patch over the item replaces the
 *   whole blob with the one key that changed, which is the defect VALE-292 /
 *   `[INVDRAW-21]` fixed: under commit-on-blur every write is a one-key patch,
 *   so for the length of the in-flight `PUT` the drawer re-derived its rows
 *   from a record holding only the field just typed. Every other value on the
 *   record read as unset — `Member number` and `DOD value` rendering their
 *   `Set …` placeholders — and the rows gated on those values (a source row is
 *   gated on its figure being present) departed and re-arrived, so the row
 *   motion overlapped them on the way past. One cause, both symptoms; the data
 *   was never affected, because the server had merged correctly all along.
 * · **A string `type` keeps the item's existing type object**, which carries the
 *   name / category / icon the request body's slug does not.
 *
 * There is deliberately **no wipe case**. The server does branch on an explicit
 * `itemInfo: null` and clears the whole blob, but {@link UpdateInventoryItemRequest}
 * types the field as non-nullable precisely so no caller here can express it —
 * an `info`-backed field clears to `''` instead. If that type ever gains
 * `| null`, the wipe belongs here, beside the merge it is the exception to.
 *
 * Keep this in step with `InventoryItemsService.updateInventoryItem`
 * (`backend/src/matters/inventory-items/`); a drift here is invisible until a
 * user watches it happen mid-commit.
 */
export function applyOptimisticInventoryUpdate(
  item: InventoryItem,
  updates: UpdateInventoryItemRequest
): InventoryItem {
  const { itemInfo: incomingItemInfo, ...rest } = updates;

  const merged = {
    ...item,
    ...rest,
    // Preserve the original type object if updates.type is a string
    type:
      updates.type && typeof updates.type === 'string'
        ? item.type // Keep original type object
        : updates.type || item.type
  } as InventoryItem;

  if (incomingItemInfo === undefined) return merged;

  return {
    ...merged,
    itemInfo: { ...(item.itemInfo ?? {}), ...incomingItemInfo }
  };
}

/**
 * Enrich inventory item with computed fields
 */
export function enrichInventoryItem(item: InventoryItem): InventoryItem {
  // Use direct value and description from backend
  return {
    ...item,
    value: item.value ?? 0,
    description: item.description || '',
    status: item.status || 'draft' // Default to 'draft' if status is missing
  };
}

/**
 * Build the enriched list-response shape used by both the client.ts fetcher
 * and the server-side `lib/data/matter-reads.ts` helper.
 *
 * Single source of truth for the inventory list aggregation — keep both
 * call sites pointed here so they don't drift.
 */
export function buildInventoryListResponse(
  items: InventoryItem[]
): InventoryItemsListResponse {
  const enriched = items.map(enrichInventoryItem);

  const byCategory: Record<string, { count: number; value: number }> = {};
  for (const item of enriched) {
    const typeSlug =
      typeof item.type === 'string' ? item.type : item.type?.slug || 'other';
    const category =
      TYPE_TO_CATEGORY[typeSlug as keyof typeof TYPE_TO_CATEGORY];
    if (category) {
      if (!byCategory[category]) {
        byCategory[category] = { count: 0, value: 0 };
      }
      byCategory[category].count++;
      byCategory[category].value += item.value || 0;
    }
  }

  return {
    items: enriched,
    total: enriched.length,
    totalValue: enriched.reduce((sum, item) => sum + (item.value || 0), 0),
    byCategory: byCategory as InventoryItemsListResponse['byCategory']
  };
}

// Cache Key Functions
// Re-export from centralized query keys for backwards compatibility

/**
 * Get cache key for inventory items list
 * @deprecated Use inventoryKeys.list from '@/lib/query-keys' instead
 */
export function getInventoryItemsCacheKey(
  matterId: number,
  filters?: Parameters<typeof inventoryKeys.list>[1]
): readonly (string | number)[] {
  return inventoryKeys.list(matterId, filters);
}
